import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { resendSignUp } from "data/mutations-common";
import { Form, Formik, FormikConfig } from "formik";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import * as Routes from "routes";
import { InferType, object, string } from "yup";
import { getUnauthErrorMessage } from "utils/cognito-auth-errors";
import { useMessageState } from "./use-message-state";

export const ResendVerificationValidationSchema = object({
  email: string().email("Enter a valid email").required("Email is required"),
});

interface ResendVerificationValues {
  email: string;
}

export const ResendVerification = () => {
  const [message, setMessage] = useMessageState({ type: null, text: "" });
  // Tracks whether we've reached a neutral "done" state (success OR an
  // account-existence error). Both must look identical so the button state
  // never reveals whether the account exists.
  const [sent, setSent] = useState(false);

  const resendVerificationMutation = useMutation({
    mutationFn: resendSignUp,
    // This is an unauthenticated flow keyed on an email, so we must not reveal
    // whether the account exists or is already confirmed. The card description
    // already states the neutral outcome, so success and account-existence
    // errors show no message; only throttling/infra errors surface one.
    onSuccess: () => {
      setMessage({ type: null, text: "" });
      setSent(true);
    },
    onError: (error) => {
      const errorText = getUnauthErrorMessage(error);
      if (errorText) {
        setMessage({ type: "error", text: errorText });
      } else {
        setMessage({ type: null, text: "" });
        setSent(true);
      }
    },
  });

  const onSubmit: FormikConfig<
    InferType<typeof ResendVerificationValidationSchema>
  >["onSubmit"] = async ({ email }) => {
    resendVerificationMutation.mutate(email);
  };

  return (
    <Formik<ResendVerificationValues>
      initialValues={{ email: "" }}
      onSubmit={onSubmit}
      validationSchema={ResendVerificationValidationSchema}
    >
      {({ values: { email }, handleChange, handleBlur, errors, touched }) => (
        <Form className="w-full max-w-sm">
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="text-xl">Resend Verification</CardTitle>
              <CardDescription>
                If an account exists for that email and still needs verifying,
                use this form to resend the verification email.
              </CardDescription>
            </CardHeader>

            <CardContent className="flex flex-col gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="text"
                  value={email}
                  onChange={(e) => {
                    // Editing the email re-arms the form for another attempt.
                    if (sent) setSent(false);
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  disabled={resendVerificationMutation.isPending}
                  className={
                    touched.email && errors.email ? "border-destructive" : ""
                  }
                />
                {touched.email && errors.email && (
                  <p className="text-xs text-destructive">{errors.email}</p>
                )}
              </div>

              {resendVerificationMutation.isPending ? (
                <div className="flex justify-center w-full py-2">
                  <Spinner />
                </div>
              ) : (
                <>
                  {message.text && message.type && (
                    <Alert
                      variant={
                        message.type === "error" ? "destructive" : "default"
                      }
                    >
                      <AlertDescription>{message.text}</AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    disabled={resendVerificationMutation.isPending || sent}
                    className="w-full"
                  >
                    {sent ? "Sent" : "Resend"}
                  </Button>
                </>
              )}
            </CardContent>
            <CardFooter className="justify-center">
              <Button variant="link" size="sm" asChild>
                <a href={Routes.signUp}>Sign up</a>
              </Button>
              <Button variant="link" size="sm" asChild>
                <a href={Routes.signIn}>Sign In</a>
              </Button>
              <Button variant="link" size="sm" asChild>
                <a href={Routes.resetPassword}>Reset Password</a>
              </Button>
            </CardFooter>
          </Card>
        </Form>
      )}
    </Formik>
  );
};
